import { useState } from 'preact/hooks';
import { ApiError, api } from '../../services/api.js';
import { uploadPaymentReceipt } from '../../utils/receiptUpload.js';
import { Card } from '../ui/Card.js';
import { PaymentPauseNotice } from './PaymentPauseNotice.js';

/**
 * 421 (#1528, mock-up frame 2): extra time CARii added after the return, which the customer pays with CARii's QR (the
 * penalty QR, never the payment gateway) and a receipt staff check. The platform's booking read says what is owed and
 * withholds the QR during the banks' Friday downtime (`payment_qr.paused_until`); `paused` is the page's live view of
 * the same pause. The receipt upload always stays: it reports a payment already made.
 */
export interface LateQrBlock {
  booking_id: string;
  amount_minor: number;
  status: string;
  late_payment: {
    kind: 'qr';
    receipt_status: 'under_review' | 'approved' | 'rejected' | null;
    receipt_uploaded_at?: string | null;
    receipt_reject_reason?: string | null;
  };
  payment_qr?: { url: string | null; paused_until: string | null };
}

const label = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const clock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const tidy = (s: string) => s.replace(/\s?(am|pm)$/i, ' $1');
const rm = (minor: number) => `RM ${(minor / 100).toFixed(2)}`;

export function LateQrPayment({
  latePayment,
  legs,
  paused,
  onChanged,
}: {
  latePayment: LateQrBlock;
  legs: Array<{ id: string; pickup_at: string; return_at: string }>;
  paused: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lp = latePayment.late_payment;
  const leg = legs.find((l) => l.id === latePayment.booking_id);

  async function onFile(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const fileId = await uploadPaymentReceipt(file);
      await api.post(`/bookings/${latePayment.booking_id}/late-payment/receipt`, {
        file_id: fileId,
      });
      onChanged();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Your receipt could not be uploaded. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (latePayment.status !== 'awaiting_payment' || lp.receipt_status === 'approved') {
    return (
      <p data-testid="late-qr-payment" className="mt-4 text-sm text-green-300">
        Extra time paid. Thank you.
      </p>
    );
  }

  if (lp.receipt_status === 'under_review') {
    return (
      <div data-testid="late-qr-payment" className="mt-4">
        <Card>
          <p className="font-semibold text-text">Receipt under review</p>
          <p className="mt-1 text-sm text-gray-400">
            Receipt sent
            {lp.receipt_uploaded_at
              ? ` ${tidy(label.format(new Date(lp.receipt_uploaded_at)))}`
              : ''}
            . We will tell you when it is approved.
          </p>
        </Card>
      </div>
    );
  }

  const qr = latePayment.payment_qr;
  const pausedNow = paused || Boolean(qr?.paused_until);
  return (
    <div data-testid="late-qr-payment" className="mt-4">
      <Card>
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">Payment due</p>
        <p className="mt-1 text-sm text-text">
          {leg
            ? `Extra time ${tidy(label.format(new Date(leg.pickup_at)))} – ${tidy(clock.format(new Date(leg.return_at)))}`
            : 'Extra time after your return'}
        </p>
        <div className="mt-3 text-center">
          <p className="font-semibold text-text">Pay {rm(latePayment.amount_minor)} to CARii</p>
          {pausedNow ? (
            <PaymentPauseNotice className="mt-2" />
          ) : qr?.url ? (
            <>
              <img
                data-testid="late-qr"
                src={qr.url}
                alt="CARii payment QR"
                className="mx-auto my-3 h-48 w-48 rounded-lg bg-white object-contain"
              />
              <p className="text-xs text-gray-400">
                Scan with your banking app. Then upload your receipt below.
              </p>
            </>
          ) : (
            <p className="mt-2 text-sm text-gray-400">
              Payment details are not available yet. Please contact support.
            </p>
          )}
        </div>
        {lp.receipt_status === 'rejected' && (
          <p className="mt-3 rounded-lg bg-red-900/40 px-3 py-2 text-sm text-red-100">
            Why it was rejected: {lp.receipt_reject_reason ?? ''}
          </p>
        )}
      </Card>
      <label
        data-testid="late-upload"
        className={`mt-3 block w-full cursor-pointer rounded-full bg-primary py-3 text-center font-bold text-black ${busy ? 'opacity-60' : ''}`}
      >
        {busy
          ? 'Uploading…'
          : lp.receipt_status === 'rejected'
            ? 'Upload a new receipt'
            : 'Upload receipt'}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          disabled={busy}
          onChange={onFile}
        />
      </label>
      <p className="mt-2 text-center text-xs text-gray-400">
        We check it and tell you once it is approved.
      </p>
      {error && (
        <p role="alert" className="mt-2 text-center text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
}
